<template>
  <div class="page-container">
    <congestion-filters ref="congestionFilter" @update="applyFilter"/>

    <b-row>
      <b-col cols="6" class="d-flex">
        <congestion-vessels
          class="w-100"
          title="Current congested vessels"
          :vessels="vessels">

          <template #header-controls-extra>
            <div class="header-splitter"></div>

            <div v-if="!!dataExport.types.length && dataExport.access.csv">
              <b-button
                v-for="(exportType, index) in dataExport.types"
                @click="handleVesselsExport(exportType)"
                :key="`type-${index}`"
                :disabled="!!getActiveRequestsCount"
                variant="primary"
                size="sm"
                class="download-button">

                <template v-if="dataExport.loading">
                  <i class="fa fa-refresh fa-spin"></i>
                </template>
                <template v-else>
                  {{ exportType.toUpperCase() }}
                </template>
              </b-button>
            </div>
          </template>

        </congestion-vessels>
      </b-col>

      <b-col cols="6">
        <b-row>
          <b-col cols="6">
            <congestion-segments-chart
              class="dashboard-card"
              title="Share by Port"
              :items="charts.port" />
          </b-col>

          <b-col cols="6">
            <congestion-segments-chart
              class="dashboard-card"
              title="Share by Commodity"
              :items="charts.commodity" />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="6">
            <congestion-segments-chart
              class="dashboard-card"
              title="Share by Segment"
              :items="charts.segment" />
          </b-col>

          <b-col cols="6">
            <congestion-segments-chart
              class="dashboard-card"
              title="Share by Load Country"
              :items="charts.loadCountry" />
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="6">
        <congestion-timeseries-chart
          title="Seasonality chart"
          chartId="seasonal"
          class="timeseries-card"
          :grouped="false"
          :seasonal="false"
          :seasonal-initial-value="true"
          :moving-avg="true"
          :stats="charts.timeseriesDefault"
        />
      </b-col>

      <b-col cols="6">
        <congestion-timeseries-chart
          title="Timeseries chart"
          chartId="timeseries"
          class="timeseries-card"
          :graph-type-selector="true"
          :grouped="true"
          :group-value="apiFilterValues.group"
          :seasonal="false"
          :seasonal-initial-value="false"
          :moving-avg="true"
          :stats="charts.timeseries"
          :export-types="dataExport.types"
          :export-loading="dataExport.loading"
          @export="handleTimeseriesExport($event)"
          @update="applyFilter({group: $event.group})"
        />
      </b-col>
    </b-row>
  </div>
</template>


<script>
import getNested from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import some from 'lodash-es/some';
import { mapActions, mapGetters } from 'vuex';
import miniToastr from 'mini-toastr';

import CongestionFilters from '@/components/congestion/CongestionFilters';
import CongestionVessels from '@/components/congestion/CongestionVessels';
import CongestionSegmentsChart from '@/components/congestion/CongestionSegmentsChart';
import CongestionTimeseriesChart from '@/components/congestion/CongestionTimeseriesChart';
import moment from 'moment';
import { downloadAsFileByURL } from '@/shared/download-utils';

export default {
  name: 'congestion',

  components: {
    CongestionTimeseriesChart,
    CongestionSegmentsChart,
    CongestionVessels,
    CongestionFilters,
  },

  data() {
    return {
      apiFilterValues: {},
      vessels: [],
      dataExport: {
        types: ['csv'],
        loading: false,
        access: {
          csv: false,
        },
      },

      charts: {
        port: [],
        commodity: [],
        segment: [],
        loadCountry: [],
        seasonal: [],
        timeseries: [],
        timeseriesDefault: [],
      },

      user: {},
    };
  },

  async mounted() {
    await this.loadUserData();
  },

  methods: {
    ...mapActions('api', ['getUser']),
    ...mapActions(
      'api/congestion', [
        'getFullCongestionData',
        'getCongestionVesselsExportData',
        'getCongestionTimeseriesExportData',
      ]
    ),

    async loadUserData() {
      this.user = await this.getUser();
      this.dataExport.access.csv = this.user['https://app.oceanbolt.com/user_authorization'].permissions.includes('download:csv') || false;
    },

    async applyFilter(apiFilterValues) {
      this.apiFilterValues = {
        ...this.apiFilterValues,
        ...apiFilterValues,
      };
       window.analytics.track("CongestionSearch",this.apiFilterValues);
      // console.log('applyFilter', JSON.stringify(this.apiFilterValues, null, 2));
      if(this.filterValid(this.apiFilterValues)) {
        await this.loadCongestionData(this.apiFilterValues);
      } else {
        miniToastr.info(
          'Please specify either: region, country, port',
          'Wrong filter values detected',
          10000,
        );
        this.resetDashbordData();
      }
    },

    async loadCongestionData(apiFilterValues) {
      const response = await this.getFullCongestionData(apiFilterValues);

      this.vessels = getNested(response, 'currentVessels', []);
      this.charts.port = getNested(response, 'currentTopPorts', []);
      this.charts.commodity = getNested(response, 'currentTopCommodityGroups', []);
      this.charts.segment = getNested(response, 'currentTopSubSegments', []);
      this.charts.loadCountry = getNested(response, 'currentTopLoadCountries', []);
      this.charts.timeseriesDefault = [getNested(response, 'timeseriesDefault', {})];
      this.charts.timeseries = getNested(response, 'timeseries', []);
    },

    filterValid(apiFilterValues) {
      return some([
        !isEmpty(apiFilterValues.regionId),
        !isEmpty(apiFilterValues.countryCode),
        !isEmpty(apiFilterValues.portId),
      ]);
    },

    async handleVesselsExport(format) {
      window.analytics.track("ExportCongestionVessels",this.apiFilterValues);
      this.dataExport.loading = true;
      if(this.filterValid(this.apiFilterValues)) {
        const apiFilterValues = {...this.apiFilterValues, format: format};
        const response = await this.getCongestionVesselsExportData(apiFilterValues);
        const timestamp = moment().format('YYYY-MM-DD-HH-mm');

        downloadAsFileByURL(
          getNested(response, 'data.csv', '--'),  // csv url
          `congestion_timeseries_export_${timestamp}.${format}`
        );
      }
      this.dataExport.loading = false;
    },

    async handleTimeseriesExport(format) {
      window.analytics.track("ExportCongestionTimeseries",this.apiFilterValues);
      this.dataExport.loading = true;
      if(this.filterValid(this.apiFilterValues)) {
        let apiFilterValues = {...this.apiFilterValues, format: format};
        const response = await this.getCongestionTimeseriesExportData(apiFilterValues);
        const timestamp = moment().format('YYYY-MM-DD-HH-mm');

        downloadAsFileByURL(
          getNested(response, 'data.csv', '--'),  // csv url
          `congestion_vessels_export_${timestamp}.${format}`
        );
      }
      this.dataExport.loading = false;
    },


    resetDashbordData() {
      this.charts.port = [];
      this.charts.commodity = [];
      this.charts.segment = [];
      this.charts.loadCountry = [];
      this.charts.timeseries = [];
      this.vessels = [];
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
  },
}
</script>


<style scoped>
.dashboard-card >>> .card-body {
  /* deep-scoped style, affects child components */
  height: 370px;
}

.timeseries-card >>> .card-body {
  height: 530px;
}

.dashboard-card >>> .chart-container {
  height: 350px;
}

.download-button,
.download-button:focus,
.download-button:active {
  min-width: 40px;
  padding: 5px 6px;
  outline: none !important;
  box-shadow: none !important;
  border: 0 !important;
}

.header-splitter {
    height: 28px;
    width: 1px;
    background-color: rgba(82, 82, 82, 0.72);
    margin: 0 10px;
}
</style>
